import { apiRequest, apiRequestBlob } from "./api";

// The filer's IANA time zone (e.g. "America/Denver") as the browser reports it.
// The API uses it to date a bank-debit (EFW) payment: the IRS requires that date
// to equal the day the IRS receives the return, counted in the taxpayer's own time
// zone. Nothing is sent if the browser can't report one; the API then refuses an
// EFW filing with a message rather than guessing a date.
function filerTimeZoneHeaders() {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return zone ? { "X-Filer-Time-Zone": zone } : {};
}

// IRS 2290 e-file: generate/validate the XML and submit the return. Only
// submit() is payment-gated on the backend (the service fee must be SUCCESS
// first) - generateXml/validateXml are callable any time.

// POST /returns/{returnId}/xml/generate
// -> { returnId, xsdValid, xml, generatedAt }. 422 if the XML fails IRS schema
// validation (error envelope carries the schema errors).
export async function generateXml(returnId) {
  return apiRequest(`/returns/${returnId}/xml/generate`, { method: "POST", headers: filerTimeZoneHeaders() });
}

// POST /returns/{returnId}/xml/validate - Review-step check. Same build + XSD
// validation as generateXml, but nothing is persisted, and the balance-due
// payment-method (EFW/EFTPS) requirement is skipped, since that choice hasn't
// been made yet at Review (PayStep comes later). Everything else - vehicles,
// credits, business data, XSD structure - is checked identically.
// -> { returnId, xsdValid, xml, generatedAt }. 422 on a schema/data error.
export async function validateXml(returnId) {
  return apiRequest(`/returns/${returnId}/xml/validate`, { method: "POST", headers: filerTimeZoneHeaders() });
}

// GET /returns/{returnId}/xml -> the stored, validated XML (404 if none yet).
export async function getXml(returnId) {
  return apiRequest(`/returns/${returnId}/xml`);
}

// POST /returns/{returnId}/submit -> regenerates + validates, marks the return
// submitted. -> { returnId, status, irsStatus, submissionId, submittedAt }
export async function submitReturn(returnId) {
  return apiRequest(`/returns/${returnId}/submit`, { method: "POST", headers: filerTimeZoneHeaders() });
}

// GET /returns/{returnId}/pdf (Return2290PdfController) -> the filled Form
// 2290 PDF, covering Page1, Page2, and both Schedule 1 copies. -> { blob,
// filename }. 409 if PDF generation isn't supported for this return (e.g. a
// foreign-address filer).
export async function getReturnPdf(returnId) {
  return apiRequestBlob(`/returns/${returnId}/pdf`, { fallbackFilename: `Form2290_${returnId}.pdf` });
}

// GET /returns/{returnId}/schedule1-pdf (Return2290PdfController) -> the
// actual IRS-stamped Schedule 1 for this return's submission, fetched
// out-of-band by the scheduler once accepted - not the copy generated by
// getReturnPdf above. -> { blob, filename }. 409 if the return hasn't been
// accepted/enqueued yet, or the Schedule 1 hasn't been fetched from the IRS yet.
export async function getSchedule1Pdf(returnId) {
  return apiRequestBlob(`/returns/${returnId}/schedule1-pdf`, { fallbackFilename: `Schedule1_${returnId}.pdf` });
}